import { CloudSyncOutlined, RetweetOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Empty, Flex, List, Space, Typography } from 'antd';
import { type IntlShape, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { useAppRefreshHandler } from '@/app/shell/app-refresh';
import { inventorizeReviewPath } from '@/app/shell/routes';
import { readSessionMeterSummary } from '@/components/MeterRecordSummary';
import { StatsRow } from '@/components/StatsRow';
import { messages, syncQueueStatusMessages } from '@/features/sync/messages';
import { isRunnableSyncRecord, useSyncCenter } from '@/features/sync/useSyncCenter';
import { SyncRecordRow } from '@/routers/sync/components/SyncCenterRecords';
import type { CaptureSession } from '@/shared/capture/capture-session';
import {
  isRetryableSyncQueueRecord,
  isReviewSyncQueueStatus,
  readSyncErrorMessage,
} from '@/shared/db/repositories/sync-queue-records';
import { SYNC_QUEUE_STATUSES } from '@/shared/db/schema';

export function SyncCenterView() {
  const intl = useIntl();
  const { activeLocation } = useStartupSession();
  const {
    currentSyncSessionId,
    error,
    isLoading,
    isSyncing,
    records,
    refresh,
    retryAll,
    retryOne,
    runSync,
    sessions,
    storageHealth,
    syncProgress,
    syncTotalCount,
  } = useSyncCenter({
    locationId: activeLocation?.locationId,
  });
  const history = useHistory();
  const sessionsById = new Map(sessions.map(session => [session.id, session]));
  const failedCount = records.filter(record => record.status === SYNC_QUEUE_STATUSES.syncFailed).length;
  const pendingCount = records.filter(
    record => record.status === SYNC_QUEUE_STATUSES.queued || record.status === SYNC_QUEUE_STATUSES.syncFailed,
  ).length;
  const retryableCount = records.filter(isRetryableSyncQueueRecord).length;
  const syncNowCount = records.filter(isRunnableSyncRecord).length;
  const syncedCount = records.filter(record => record.status === SYNC_QUEUE_STATUSES.synced).length;
  const needsReviewCount = records.filter(record => isReviewSyncQueueStatus(record.status)).length;
  const syncNowLabel = intl.formatMessage(messages.syncSyncNow);
  const retryFailedReviewLabel = intl.formatMessage(messages.syncRetryFailedReview);
  const isBusy = isLoading || isSyncing;
  useAppRefreshHandler(refresh);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small" style={{ position: 'sticky', top: 8, zIndex: 3 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Flex gap="small" wrap>
            <Button
              disabled={syncNowCount === 0 || isBusy}
              icon={<CloudSyncOutlined />}
              loading={isSyncing}
              onClick={() => void runSync()}
              style={{ flex: '1 1 200px', height: 'auto', padding: '8px 12px', whiteSpace: 'normal' }}
              title={syncNowLabel}
              type="primary"
            >
              {syncNowLabel}
            </Button>
            <Button
              disabled={retryableCount === 0 || isBusy}
              icon={<RetweetOutlined />}
              onClick={() => void retryAll()}
              style={{ flex: '1 1 200px', height: 'auto', padding: '8px 12px', whiteSpace: 'normal' }}
              title={retryFailedReviewLabel}
            >
              {retryFailedReviewLabel}
            </Button>
          </Flex>
        </Space>
      </Card>

      {error ? <Alert closable message={error} showIcon type="error" /> : null}

      {isSyncing || syncProgress.length > 0 ? (
        <SyncProgressPanel
          currentSyncSessionId={currentSyncSessionId}
          intl={intl}
          progress={syncProgress}
          sessionsById={sessionsById}
          total={syncTotalCount}
        />
      ) : null}

      <StatsRow
        items={[
          { label: intl.formatMessage(messages.syncPending), value: pendingCount },
          { label: intl.formatMessage(messages.syncFailed), value: failedCount },
          { label: intl.formatMessage(messages.syncNeedsReview), value: needsReviewCount },
          { label: intl.formatMessage(messages.syncSynced), value: syncedCount },
          { label: intl.formatMessage(messages.syncPhotos), value: storageHealth?.photoCount ?? 0 },
          { label: intl.formatMessage(messages.syncDrafts), value: records.length - syncedCount },
        ]}
      />

      <Card size="small" title={intl.formatMessage(messages.syncQueueRecords)}>
        {records.length === 0 ? (
          <Empty
            description={intl.formatMessage(messages.syncEmptyQueueDescription)}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={records}
            loading={isLoading}
            renderItem={record => (
              <SyncRecordRow
                key={record.id}
                onOpen={() => history.push(inventorizeReviewPath(record.sessionId))}
                onRetry={() => void retryOne(record.sessionId)}
                record={record}
                session={sessionsById.get(record.sessionId)}
              />
            )}
          />
        )}
      </Card>
    </Space>
  );
}

function SyncProgressPanel({
  currentSyncSessionId,
  intl,
  progress,
  sessionsById,
  total,
}: {
  currentSyncSessionId: string | null;
  intl: IntlShape;
  progress: Array<{ errorCode?: string; sessionId?: string; status: string; updatedAt: string }>;
  sessionsById: Map<string, CaptureSession>;
  total: number;
}) {
  const waiting = Math.max(0, total - progress.length - (currentSyncSessionId ? 1 : 0));

  return (
    <Card size="small" title={intl.formatMessage(messages.syncProgressTitle)}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Typography.Text type="secondary">
          {intl.formatMessage(messages.syncProgressDescription, {
            done: progress.length,
            total,
            waiting,
          })}
        </Typography.Text>
        {currentSyncSessionId ? (
          <Typography.Text>
            {intl.formatMessage(messages.syncProgressCurrent, {
              item: formatProgressSession(currentSyncSessionId, sessionsById, intl),
            })}
          </Typography.Text>
        ) : null}
        <List
          dataSource={progress}
          locale={{ emptyText: intl.formatMessage(messages.syncProgressEmpty) }}
          renderItem={entry => {
            const session = entry.sessionId ? sessionsById.get(entry.sessionId) : undefined;
            const errorMessage = readSyncErrorMessage(
              {
                errorCode: entry.errorCode,
                session,
                status: entry.status,
              },
              intl,
            );

            return (
              <List.Item>
                <Space direction="vertical" size={0}>
                  <Typography.Text>
                    {entry.sessionId ? formatProgressSession(entry.sessionId, sessionsById, intl) : entry.status}
                  </Typography.Text>
                  <Typography.Text type={entry.status === 'failed' ? 'danger' : 'secondary'}>
                    {[formatProgressStatus(entry.status, intl), errorMessage].filter(Boolean).join(' · ')}
                  </Typography.Text>
                </Space>
              </List.Item>
            );
          }}
          size="small"
        />
      </Space>
    </Card>
  );
}

function formatProgressSession(sessionId: string, sessionsById: Map<string, CaptureSession>, intl: IntlShape) {
  const session = sessionsById.get(sessionId);

  return session ? readSessionMeterSummary(session, `Draft ${sessionId.slice(0, 14)}`, intl).title : sessionId;
}

function formatProgressStatus(status: string, intl: IntlShape) {
  if (status === 'failed') {
    return intl.formatMessage(messages.syncFailed);
  }

  if (status in syncQueueStatusMessages) {
    return intl.formatMessage(syncQueueStatusMessages[status as keyof typeof syncQueueStatusMessages]);
  }

  return status;
}
